import { lazy, Suspense, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './api/queryClient'
import { AuthContext, useAuthState, useAuth } from './hooks/useAuth'
import { StepUpProvider } from './hooks/useStepUp'
import { AuthModalProvider } from './hooks/useAuthModal'
import { useAuthModal } from './hooks/authModalContext'
import { getVerticalMeta, useVertical } from './components/layout/useVertical'
import { AppShell } from './components/layout/AppShell'
import { DiscoverySearchProvider } from './components/layout/DiscoverySearchContext'
import { VerticalProvider } from './components/layout/VerticalContext'
import { ErrorBoundary } from './components/common/ErrorBoundary'
import { ThriftSearchLayout } from './components/thrifts/ThriftDiscoveryFrame'
import { MovesSearchLayout } from './components/moves/MovesDiscoveryFrame'
import { LoadingSpinner } from './components/common/LoadingSpinner'
import UnauthorizedPage from './pages/shared/UnauthorizedPage'

// Lazy-loaded pages — code-split per role bundle
const VerticalLauncher = lazy(() => import('./pages/public/VerticalLauncher'))
const LandingPage = lazy(() => import('./pages/public/LandingPage'))
const ExploreFarmsPage = lazy(() => import('./pages/public/ExploreFarmsPage'))
const PublicMarketplacePage = lazy(() => import('./pages/public/PublicMarketplacePage'))
const UpcomingEventsPage = lazy(() => import('./pages/public/UpcomingEventsPage'))
const CreationPage = lazy(() => import('./pages/public/CreationPage'))
const ExperienceDetailPage = lazy(() => import('./pages/public/ExperienceDetailPage'))
const PublicHomestaysPage = lazy(() => import('./pages/public/PublicHomestaysPage'))
const HomestayDetailPage = lazy(() => import('./pages/public/HomestayDetailPage'))
const PublicThriftsPage = lazy(() => import('./pages/public/PublicThriftsPage'))
const ThriftItemDetailPage = lazy(() => import('./pages/public/ThriftItemDetailPage'))
const MovesHomePage = lazy(() => import('./pages/public/MovesHomePage'))
const MemberProfilePage = lazy(() => import('./pages/public/MemberProfilePage'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'))
const OnboardingPage = lazy(() => import('./pages/auth/OnboardingPage'))

const ConsumerHomePage = lazy(() => import('./pages/consumer/ConsumerHomePage'))
const FavoriteListingsPage = lazy(() => import('./pages/consumer/FavoriteListingsPage'))
const CartPage = lazy(() => import('./pages/consumer/CartPage'))
const CheckoutPage = lazy(() => import('./pages/consumer/CheckoutPage'))
const CheckoutPaymentPage = lazy(() => import('./pages/consumer/CheckoutPaymentPage'))
const ListingDetailPage = lazy(() => import('./pages/consumer/ListingDetailPage'))
const OrderListPage = lazy(() => import('./pages/consumer/OrderListPage'))
const OrderDetailPage = lazy(() => import('./pages/consumer/OrderDetailPage'))
const AnnouncementFeedPage = lazy(() => import('./pages/consumer/AnnouncementFeedPage'))
const ReservationsPage = lazy(() => import('./pages/consumer/ReservationsPage'))
const HomestayBookingsPage = lazy(() => import('./pages/consumer/HomestayBookingsPage'))
const ThriftOffersPage = lazy(() => import('./pages/consumer/ThriftOffersPage'))
const ThriftPurchasesPage = lazy(() => import('./pages/consumer/ThriftPurchasesPage'))
const BookMovePage = lazy(() => import('./pages/consumer/BookMovePage'))
const MyMovesPage = lazy(() => import('./pages/consumer/MyMovesPage'))
const MoveDetailPage = lazy(() => import('./pages/consumer/MoveDetailPage'))
const HomestayBookingDetailPage = lazy(() => import('./pages/consumer/HomestayBookingDetailPage'))
const RaiseDisputePage = lazy(() => import('./pages/consumer/RaiseDisputePage'))
const OrderTrackingPage = lazy(() => import('./pages/consumer/OrderTrackingPage'))

const MyListingsPage = lazy(() => import('./pages/creator/MyListingsPage'))
const CreatorHomePage = lazy(() => import('./pages/creator/CreatorHomePage'))
const CreateListingPage = lazy(() => import('./pages/creator/CreateListingPage'))
const EditListingPage = lazy(() => import('./pages/creator/EditListingPage'))
const PickupOrdersPage = lazy(() => import('./pages/creator/PickupOrdersPage'))
const DemandDashboardPage = lazy(() => import('./pages/creator/DemandDashboardPage'))
const MyAnnouncementsPage = lazy(() => import('./pages/creator/MyAnnouncementsPage'))
const CreateAnnouncementPage = lazy(() => import('./pages/creator/CreateAnnouncementPage'))
const EditAnnouncementPage = lazy(() => import('./pages/creator/EditAnnouncementPage'))
const ReviewsPage = lazy(() => import('./pages/creator/ReviewsPage'))
const CreatorBlogsPage = lazy(() => import('./pages/creator/CreatorBlogsPage'))
const CreatorGrowingPage = lazy(() => import('./pages/creator/CreatorGrowingPage'))
const CreatorExperiencesPage = lazy(() => import('./pages/creator/CreatorExperiencesPage'))
const HostHomestaysPage = lazy(() => import('./pages/creator/HostHomestaysPage'))
const HostHomestayManagePage = lazy(() => import('./pages/creator/HostHomestayManagePage'))
const SellThriftListingsPage = lazy(() => import('./pages/creator/SellThriftListingsPage'))
const CreateThriftListingPage = lazy(() => import('./pages/creator/CreateThriftListingPage'))
const SellThriftListingManagePage = lazy(
  () => import('./pages/creator/SellThriftListingManagePage'),
)
const CreatorPublicPageSettingsPage = lazy(
  () => import('./pages/creator/CreatorPublicPageSettingsPage'),
)

const FulfillerStatsPage = lazy(() => import('./pages/fulfiller/FulfillerStatsPage'))
const FulfillerHistoryPage = lazy(() => import('./pages/fulfiller/FulfillerHistoryPage'))
const FulfillerHomePage = lazy(() => import('./pages/fulfiller/FulfillerHomePage'))
const AvailableJobsPage = lazy(() => import('./pages/fulfiller/AvailableJobsPage'))
const ThriftDeliveriesPage = lazy(() => import('./pages/fulfiller/ThriftDeliveriesPage'))
const FulfillerTripsPage = lazy(() => import('./pages/fulfiller/FulfillerTripsPage'))
const ActiveTripPage = lazy(() => import('./pages/fulfiller/ActiveTripPage'))
const TripStopPage = lazy(() => import('./pages/fulfiller/TripStopPage'))
const ReportIncidentPage = lazy(() => import('./pages/fulfiller/ReportIncidentPage'))
const VehicleProfilePage = lazy(() => import('./pages/fulfiller/VehicleProfilePage'))
const CarrierMovePage = lazy(() => import('./pages/fulfiller/CarrierMovePage'))
const BecomeFulfillerPage = lazy(() => import('./pages/profile/BecomeFulfillerPage'))

const SecuritySettingsPage = lazy(() => import('./pages/shared/SecuritySettingsPage'))
const MessagesPage = lazy(() => import('./pages/shared/MessagesPage'))
const SellerAnalyticsPage = lazy(() => import('./pages/analytics/SellerAnalyticsPage'))
const ReceiptQueuePage = lazy(() => import('./pages/ops/ReceiptQueuePage'))
const OpsHomestaysPage = lazy(() => import('./pages/ops/OpsHomestaysPage'))
const OpsThriftModerationPage = lazy(() => import('./pages/ops/OpsThriftModerationPage'))
const HubConsolePage = lazy(() => import('./pages/ops/HubConsolePage'))
const OpsThriftDisputesPage = lazy(() => import('./pages/ops/OpsThriftDisputesPage'))
const BecomeSellerPage = lazy(() => import('./pages/creator/BecomeSellerPage'))
const FloatDashboardPage = lazy(() => import('./pages/ops/FloatDashboardPage'))
const DisbursementsPage = lazy(() => import('./pages/ops/DisbursementsPage'))
const EscalationsPage = lazy(() => import('./pages/ops/EscalationsPage'))
const ManualConfirmPage = lazy(() => import('./pages/ops/ManualConfirmPage'))
const DisputeQueuePage = lazy(() => import('./pages/ops/DisputeQueuePage'))
const DisputeDetailPage = lazy(() => import('./pages/ops/DisputeDetailPage'))
const IdVerificationQueuePage = lazy(() => import('./pages/ops/IdVerificationQueuePage'))
const ParticipantManagementPage = lazy(() => import('./pages/ops/ParticipantManagementPage'))
const TransactionSearchPage = lazy(() => import('./pages/ops/TransactionSearchPage'))
const IncidentQueuePage = lazy(() => import('./pages/ops/IncidentQueuePage'))
const IncidentDetailPage = lazy(() => import('./pages/ops/IncidentDetailPage'))
const PlatformHealthPage = lazy(() => import('./pages/ops/PlatformHealthPage'))
const ActiveTripsQueuePage = lazy(() => import('./pages/ops/ActiveTripsQueuePage'))
const ExceptionQueuesPage = lazy(() => import('./pages/ops/ExceptionQueuesPage'))
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminAccessControlPage = lazy(() => import('./pages/admin/AdminAccessControlPage'))
const AdminMasterDataPage = lazy(() => import('./pages/admin/AdminMasterDataPage'))
const AdminAuditLogPage = lazy(() => import('./pages/admin/AdminAuditLogPage'))

const NotificationFeedPage = lazy(() => import('./pages/shared/NotificationFeedPage'))
const ActivityHistoryPage = lazy(() => import('./pages/shared/ActivityHistoryPage'))
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'))
const EarningsPage = lazy(() => import('./pages/profile/EarningsPage'))
const SubscriptionPage = lazy(() => import('./pages/profile/SubscriptionPage'))

const ROLE_HOME_PATHS: Record<string, string> = {
  consumer: '/consumer',
  creator: '/creator',
  fulfiller: '/fulfiller',
  ops: '/ops/health',
  admin: '/admin',
}

/** Provides the discovery search state so the header search bar (below the tabs)
 *  and the page's filtering share one context. Must sit above the page, since the
 *  page calls useDiscoverySearch in its own body and renders the header as a child. */
function DiscoverySearchLayout() {
  return (
    <DiscoverySearchProvider>
      <Outlet />
    </DiscoverySearchProvider>
  )
}

/**
 * Gate for authenticated routes.
 *
 * An unauthenticated visitor is put on the vertical's public home with the
 * sign-in popup open, rather than redirected to the `/login` page. There is no
 * regression in losing the intended destination: `routeAfterLogin` already sends
 * you to your role home regardless of where you were headed.
 *
 * Only a visitor who was *never* signed in gets prompted. Signing out happens on
 * a protected route, inside this gate — so an unconditional prompt fired on the
 * way out and left the popup sitting over the public home the user had just been
 * dropped on. Session expiry still prompts, from `api/client.ts`.
 */
export function RequireAuth() {
  const { isAuthenticated, isBootstrapping } = useAuth()
  const { openAuthModal } = useAuthModal()
  const { activeVertical } = useVertical()
  const hadSession = useRef(isAuthenticated)

  useEffect(() => {
    if (isBootstrapping) return
    if (isAuthenticated) {
      hadSession.current = true
      return
    }
    if (!hadSession.current) openAuthModal()
  }, [isAuthenticated, isBootstrapping, openAuthModal])

  // Tokens are memory-only, so a reload lands here signed out while the refresh
  // cookie is still being exchanged. Redirecting now would throw a signed-in user
  // off their own page every time they hit F5.
  if (isBootstrapping) return <LoadingSpinner />

  if (!isAuthenticated) return <Navigate to={getVerticalMeta(activeVertical).home} replace />
  return <Outlet />
}

/** Renders children only if activeRole matches. Otherwise renders nothing. */
export function RequireRole({ requiredRole }: { requiredRole: string }) {
  const { roles, activeRole } = useAuth()
  if (!roles.includes(requiredRole)) {
    return <UnauthorizedPage requiredRole={requiredRole} activeRole={activeRole} />
  }
  if (activeRole !== requiredRole) {
    return <Navigate to={ROLE_HOME_PATHS[activeRole ?? ''] ?? '/notifications'} replace />
  }
  return <Outlet />
}

export function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/" element={<VerticalLauncher />} />
        {/* Same page the landing-only build serves at osle.app/vision — kept on both
            paths so the public URL is identical whichever bundle is deployed. */}
        <Route path="/vision" element={<LandingPage />} />
        <Route path="/osle-landing" element={<LandingPage />} />
        <Route element={<DiscoverySearchLayout />}>
          <Route path="/explore-farms" element={<ExploreFarmsPage />} />
          <Route path="/marketplace" element={<PublicMarketplacePage />} />
          <Route path="/upcoming-events" element={<UpcomingEventsPage />} />
        </Route>
        <Route path="/creations/:slug" element={<CreationPage />} />
        <Route path="/experiences/:packageId" element={<ExperienceDetailPage />} />
        <Route path="/homestays" element={<PublicHomestaysPage />} />
        <Route path="/homestays/:propertyId" element={<HomestayDetailPage />} />
        {/* Shared layout so the search pill's query survives opening an item. */}
        <Route element={<ThriftSearchLayout />}>
          <Route path="/thrifts" element={<PublicThriftsPage />} />
          <Route path="/thrifts/:itemId" element={<ThriftItemDetailPage />} />
          {/* Inside the thrift layout so the member page keeps the same header and
              search pill a buyer arrived with. */}
          <Route path="/members/:memberId" element={<MemberProfilePage />} />
        </Route>
        {/* Shared layout so the Moves search pill query survives navigation. */}
        <Route element={<MovesSearchLayout />}>
          <Route path="/moves" element={<MovesHomePage />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<RequireAuth />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route element={<AppShell />}>
            {/* Available to every signed-in role — ops/admin have no other way to
                set the transaction PIN the step-up guard now requires (DEF-29). */}
            <Route path="/settings/security" element={<SecuritySettingsPage />} />
            {/* Seller insights — one route for every vertical; the page reads the
                active vertical and the server describes that vertical's metrics. */}
            <Route path="/insights" element={<SellerAnalyticsPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/become-seller" element={<BecomeSellerPage />} />
            <Route path="/become-fulfiller" element={<BecomeFulfillerPage />} />

            {/* Consumer routes */}
            <Route element={<RequireRole requiredRole="consumer" />}>
              <Route path="/consumer" element={<ConsumerHomePage />} />
              <Route path="/favorites" element={<FavoriteListingsPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/:id/pay" element={<CheckoutPaymentPage />} />
              <Route path="/orders" element={<OrderListPage />} />
              <Route path="/orders/:orderId" element={<OrderDetailPage />} />
              <Route path="/orders/:orderId/track" element={<OrderTrackingPage />} />
              <Route path="/orders/:orderId/dispute" element={<RaiseDisputePage />} />
              <Route path="/announcements" element={<AnnouncementFeedPage />} />
              <Route path="/reservations" element={<ReservationsPage />} />
              <Route path="/homestay-bookings" element={<HomestayBookingsPage />} />
              <Route path="/homestay-bookings/:bookingId" element={<HomestayBookingDetailPage />} />
              <Route path="/thrifts/offers" element={<ThriftOffersPage />} />
              <Route path="/thrifts/purchases" element={<ThriftPurchasesPage />} />
              <Route path="/moves/book" element={<BookMovePage />} />
              <Route path="/moves/mine" element={<MyMovesPage />} />
              <Route path="/moves/:requestId" element={<MoveDetailPage />} />
            </Route>

            {/* Creator routes */}
            <Route element={<RequireRole requiredRole="creator" />}>
              <Route path="/creator" element={<CreatorHomePage />} />
              <Route path="/my-listings" element={<MyListingsPage />} />
              <Route path="/my-listings/new" element={<CreateListingPage />} />
              <Route path="/my-listings/:itemId/edit" element={<EditListingPage />} />
              <Route path="/pickups" element={<PickupOrdersPage />} />
              <Route path="/my-reviews" element={<ReviewsPage />} />
              <Route path="/creator/blogs" element={<CreatorBlogsPage />} />
              <Route path="/creator/growing" element={<CreatorGrowingPage />} />
              <Route path="/creator/experiences" element={<CreatorExperiencesPage />} />
              <Route path="/host/homestays" element={<HostHomestaysPage />} />
              <Route path="/host/homestays/:propertyId" element={<HostHomestayManagePage />} />
              <Route path="/sell/listings" element={<SellThriftListingsPage />} />
              <Route path="/sell/become-seller" element={<BecomeSellerPage />} />
              <Route path="/sell/new" element={<CreateThriftListingPage />} />
              <Route path="/sell/listings/:listingId" element={<SellThriftListingManagePage />} />
              <Route path="/creator/public-page" element={<CreatorPublicPageSettingsPage />} />
              <Route path="/demand" element={<DemandDashboardPage />} />
              <Route path="/my-announcements" element={<MyAnnouncementsPage />} />
              <Route path="/my-announcements/new" element={<CreateAnnouncementPage />} />
              <Route
                path="/my-announcements/:announcementId/edit"
                element={<EditAnnouncementPage />}
              />
            </Route>

            {/* Fulfiller routes */}
            <Route element={<RequireRole requiredRole="fulfiller" />}>
              <Route path="/fulfiller" element={<FulfillerHomePage />} />
              <Route path="/fulfiller/jobs" element={<AvailableJobsPage />} />
              <Route path="/fulfiller/thrift-deliveries" element={<ThriftDeliveriesPage />} />
              <Route path="/fulfiller/trips" element={<FulfillerTripsPage />} />
              <Route path="/fulfiller/trips/:tripId" element={<ActiveTripPage />} />
              <Route path="/fulfiller/trips/:tripId/stops/:stopId" element={<TripStopPage />} />
              <Route path="/fulfiller/incidents/new" element={<ReportIncidentPage />} />
              <Route path="/fulfiller/vehicle" element={<VehicleProfilePage />} />
              <Route path="/fulfiller/moves/:requestId" element={<CarrierMovePage />} />
              <Route path="/fulfiller/stats" element={<FulfillerStatsPage />} />
              <Route path="/fulfiller/history" element={<FulfillerHistoryPage />} />
            </Route>

            {/* Ops routes */}
            <Route element={<RequireRole requiredRole="ops" />}>
              <Route path="/ops/verification" element={<ReceiptQueuePage />} />
              <Route path="/ops/homestays" element={<OpsHomestaysPage />} />
              <Route path="/ops/thrifts" element={<OpsThriftModerationPage />} />
              <Route path="/ops/hub-console" element={<HubConsolePage />} />
              <Route path="/ops/thrift-disputes" element={<OpsThriftDisputesPage />} />
              <Route path="/ops/float" element={<FloatDashboardPage />} />
              <Route path="/ops/disbursements" element={<DisbursementsPage />} />
              <Route path="/ops/disputes" element={<DisputeQueuePage />} />
              <Route path="/ops/disputes/:disputeId" element={<DisputeDetailPage />} />
              <Route path="/ops/escalations" element={<EscalationsPage />} />
              <Route path="/ops/confirm" element={<ManualConfirmPage />} />
              <Route path="/ops/id-verification" element={<IdVerificationQueuePage />} />
              <Route path="/ops/participants" element={<ParticipantManagementPage />} />
              <Route path="/ops/transactions" element={<TransactionSearchPage />} />
              <Route path="/ops/incidents" element={<IncidentQueuePage />} />
              <Route path="/ops/incidents/:incidentId" element={<IncidentDetailPage />} />
              <Route path="/ops/health" element={<PlatformHealthPage />} />
              <Route path="/ops/trips" element={<ActiveTripsQueuePage />} />
              <Route path="/ops/exceptions" element={<ExceptionQueuesPage />} />
            </Route>

            {/* Admin routes */}
            <Route element={<RequireRole requiredRole="admin" />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/access" element={<AdminAccessControlPage />} />
              <Route path="/admin/access/:participantId" element={<AdminAccessControlPage />} />
              <Route path="/admin/master-data" element={<AdminMasterDataPage />} />
              <Route path="/admin/audit" element={<AdminAuditLogPage />} />
            </Route>

            {/* Shared routes (all authenticated users) */}
            <Route path="/listings/:itemId" element={<ListingDetailPage />} />
            <Route path="/notifications" element={<NotificationFeedPage />} />
            <Route path="/history" element={<ActivityHistoryPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/earnings" element={<EarningsPage />} />
            <Route path="/subscriptions" element={<SubscriptionPage />} />
          </Route>
        </Route>

        {/* Catch-all: redirect to login */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  const auth = useAuthState()

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthContext.Provider value={auth}>
          <VerticalProvider>
            <StepUpProvider>
              <BrowserRouter>
                {/* Inside the router: AuthPanel navigates on successful login. */}
                <AuthModalProvider>
                  <AppRoutes />
                </AuthModalProvider>
              </BrowserRouter>
            </StepUpProvider>
          </VerticalProvider>
        </AuthContext.Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
